/**
 * Générateur — Il utilise la racine carrée d'un nombre positif en lien avec des
 * situations géométriques (théorème de Pythagore ; agrandissement, réduction et
 * aires).
 * Attendu ID : 151 | Niveau : 4ème | Domaine : Nombres (desc 11, O16)
 *
 * Source doc prof (NOMBRE ET CALCUL, cluster 3) : Ex.17-19 « carré d'aire A → côté
 * √A », Ex.20-21 « disque d'aire A → rayon √(A/π) » (coquille source « rayon 2 cm² »
 * corrigée en aire), libellé = Pythagore + aires. Plan validé 2026-07-14 (texte
 * seul, sans figure SVG — décision utilisatrice), revu 2026-09-22 :
 *
 *   Carte « cote_carre »     — côté d'un carré connaissant son aire (saisie, arrondi au mm)
 *   Carte « pythagore »      — hypoténuse par Pythagore, arrondi au dixième (saisie)
 *   Carte « disque »         — rayon d'un disque connaissant son aire (saisie, arrondi)
 *   Carte « calcul_cote »    — quel calcul donne le côté d'un carré d'aire A ? (QCM)
 *   Carte « agrandissement » — l'aire d'un carré est multipliée (ou divisée) par un
 *                              carré parfait : par combien le côté l'est-il ? (saisie)
 *
 * Arrondis EXACTS par isqrt entier (carré, Pythagore). Le disque emploie π
 * (Math.PI) avec garde anti-frontière d'arrondi → gen et solveur (mêmes calculs)
 * concordent de façon déterministe.
 *
 * Revue 2026-09-22 : les trois saisies approchées demandent « l'arrondi » et non
 * plus « une valeur approchée » (une troncature est une valeur approchée
 * légitime, refusée une fois sur deux). Carte « formule » SUPPRIMÉE et remplacée
 * par « calcul_cote » : elle ne contenait aucune racine carrée alors que
 * l'attendu porte sur la racine en situation, et deux de ses distracteurs
 * avaient la même valeur que deux autres. Carte « pythagore » : le cas exact et
 * son pool de neuf couples figés sont RETIRÉS (pool interdit, et ce cas refaisait
 * la carte « valeur » de l'attendu 140 habillée en géométrie) ; ne reste que le
 * cas approché, ce qui supprime aussi sa consigne à deux branches. Carte
 * « agrandissement » AJOUTÉE : l'intitulé la mentionne, aucune carte ne la
 * portait. Parenthèses explicatives devenues des phrases.
 *
 * Revue 2026-09-23 (typographie, règle prof) : toute expression AFFICHÉE passe
 * en KaTeX : les longueurs données de « pythagore » (AB = 5, AC = 3, BC), le π
 * du rappel de « disque », les trois calculs faux du QCM « calcul_cote » (÷ et ×
 * en \div et \times, la racine juste l'était déjà). Les « cm² » sont des
 * symboles d'unité, ils restent en texte ; les réponses tapées ne changent pas.
 *
 * Calculatrice 2026-09-23 : les TROIS cartes qui demandent une valeur arrondie
 * (côté d'un carré, Pythagore, rayon d'un disque) portent
 * contenu.calculatrice : ce sont des racines de nombres qui ne sont pas des
 * carrés parfaits, et le disque mobilise π. Le QCM « quel calcul donne le
 * côté » et la carte d'agrandissement ne calculent rien, elles restent sans.
 */

import { randomChoice, randInt, makeSaisie, makeQcm, racineTex, tex } from '../../../utils';
import { estCarreParfait } from './nombres';

const ATTENDU_ID = 151;

const L_COTE = 'Côté d\'un carré connaissant son aire';
const L_PYTHAGORE = 'Longueur d\'un côté par le théorème de Pythagore';
const L_DISQUE = 'Rayon d\'un disque connaissant son aire';
const L_CALCUL = 'Choisir le calcul qui donne le côté d\'un carré';
const L_AGRANDISSEMENT = 'Agrandir ou réduire un carré : aire et côté';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const isqrt = (m) => { let a = Math.floor(Math.sqrt(m)); while ((a + 1) * (a + 1) <= m) a++; while (a * a > m) a--; return a; };
const arrondiR = (n, k) => { const M = n * (10 ** (2 * k)); const a = isqrt(M); return (M - a * a >= a + 1) ? a + 1 : a; };
const fmtApprox = (R, k) => `${Math.floor(R / 10 ** k)},${String(R % 10 ** k).padStart(k, '0')}`;

// ═════════ Carte « cote_carre » — côté = √aire, arrondi au dixième (saisie) ═════════

const genCote = () => {
    let A; let R;
    do { A = randInt(2, 200); R = arrondiR(A, 1); } while (estCarreParfait(A) || R % 10 === 0);
    const payload = makeSaisie(fmtApprox(R, 1),
        `<p>Un carré a une aire de <b>${A} cm²</b>.</p>`
        + '<p><b>Donne l\'arrondi de son côté au dixième de cm, c\'est-à-dire au millimètre.</b></p>',
        { ...opts(L_COTE), suffixe: 'cm' });
    // Calculatrice AUTORISÉE (revue 2026-09-23) : racine carrée d'un nombre qui n'est pas un carré parfait, arrondie au dixième.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.params = { A, rep: fmtApprox(R, 1) };
    return payload;
};

// ═════════ Carte « pythagore » — hypoténuse, arrondi au dixième (saisie) ═════════

const genPythagore = () => {
    // Deux côtés de l'angle droit de 2 à 12 cm, somme des carrés non carrée
    // parfaite (sinon BC serait entier, ce qui refait la carte « valeur » de 140).
    let a = 2; let b = 3; let s = 13;                                       // repli déterministe
    for (let i = 0; i < 80; i++) {
        const x = randInt(2, 12);
        const y = randInt(2, 12);
        if (!estCarreParfait(x * x + y * y)) { a = x; b = y; s = x * x + y * y; break; }
    }
    const rep = fmtApprox(arrondiR(s, 1), 1);
    const payload = makeSaisie(rep,
        `<p>Le triangle ABC est rectangle en A, avec <b>${tex(`AB = ${a}`)} cm</b> et <b>${tex(`AC = ${b}`)} cm</b>.</p>`
        + `<p><b>Donne l'arrondi de ${tex('BC')} au dixième de cm.</b></p>`,
        { ...opts(L_PYTHAGORE), suffixe: 'cm' });
    // Calculatrice AUTORISÉE (revue 2026-09-23) : hypoténuse par Pythagore : racine non entière, arrondie au dixième.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.params = { a, b, s, rep };
    return payload;
};

// ═════════ Carte « disque » — rayon = √(aire/π), arrondi au dixième (saisie) ═════════

const genDisque = () => {
    let A; let R;
    do {
        A = randInt(2, 400);
        const r = Math.sqrt(A / Math.PI);
        const t = r * 10;
        const frac = t - Math.floor(t);
        if (frac > 0.4 && frac < 0.6) { A = -1; continue; }   // garde anti-frontière
        R = Math.round(t);
    } while (A < 0 || R % 10 === 0 || R < 3);
    const payload = makeSaisie(fmtApprox(R, 1),
        `<p>Un disque a une aire de <b>${A} cm²</b>. On rappelle que l'aire d'un disque est égale à ${tex('\\pi')} multiplié par le carré de son rayon.</p>`
        + '<p><b>Donne l\'arrondi de son rayon au dixième de cm.</b></p>',
        { ...opts(L_DISQUE), suffixe: 'cm' });
    // Calculatrice AUTORISÉE (revue 2026-09-23) : racine carrée d'un nombre qui n'est pas un carré parfait, arrondie au dixième.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.params = { A, rep: fmtApprox(R, 1) };
    return payload;
};

// ═════════ Carte « calcul_cote » — quel calcul donne le côté d'un carré ? (QCM) ═════════

// Trois erreurs nommées, fausses par construction :
//   perimetre : A ÷ 4 (le côté vu comme le quart d'un périmètre) ;
//   double    : A ÷ 2 (la racine confondue avec la moitié) ;
//   carre     : A × A (élever au carré au lieu d'extraire la racine).
// A ≠ 4 et A ≠ 16 : les seules aires pour lesquelles un calcul faux a la même
// valeur que √A (√4 = 4 ÷ 2, √16 = 16 ÷ 4). Un élève qui calculerait tout ne
// doit jamais trouver deux calculs justes.
const genCalculCote = () => {
    let A = 30;                                                             // repli déterministe
    for (let i = 0; i < 40; i++) {
        const x = randInt(2, 200);
        if (x !== 4 && x !== 16) { A = x; break; }
    }
    const correct = racineTex(A);
    // Les calculs faux sont affichés en KaTeX comme la racine juste (même police,
    // même taille : rien ne trahit la bonne option).
    const distracteurs = {
        [tex(`${A} \\div 4`)]: 'perimetre',
        [tex(`${A} \\div 2`)]: 'double',
        [tex(`${A} \\times ${A}`)]: 'carre',
    };
    const payload = makeQcm([correct, ...Object.keys(distracteurs)], correct,
        `<p>Un carré a une aire de <b>${A} cm²</b>.</p>`
        + '<p><b>Quel calcul donne la longueur de son côté, en cm ?</b></p>',
        opts(L_CALCUL));
    payload._meta.params = { A, distracteurs };
    return payload;
};

// ═════════ Carte « agrandissement » — aire × k², côté × k (saisie) ═════════

const genAgrandissement = () => {
    const k = randInt(2, 12);                        // le côté est multiplié par k, l'aire par k² (carré parfait de 4 à 144)
    const K = k * k;
    const reduction = randomChoice([false, true]);
    const texte = reduction
        ? `<p>On réduit un carré : son aire est divisée par <b>${K}</b>.</p>`
            + '<p><b>Par combien la longueur de son côté est-elle divisée ?</b></p>'
        : `<p>On agrandit un carré : son aire est multipliée par <b>${K}</b>.</p>`
            + '<p><b>Par combien la longueur de son côté est-elle multipliée ?</b></p>';
    const payload = makeSaisie(String(k), texte, opts(L_AGRANDISSEMENT));
    payload._meta.params = { k, K, reduction };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    cote_carre_texte: genCote,
    pythagore_texte: genPythagore,
    disque_texte: genDisque,
    calcul_cote_qcm_unique: genCalculCote,
    agrandissement_texte: genAgrandissement,
};

const FORMAT_LABELS = { texte: 'Saisie', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'cote_carre', label: L_COTE, formatsList: buildFormats('cote_carre') },
    { id: 'pythagore', label: L_PYTHAGORE, formatsList: buildFormats('pythagore') },
    { id: 'disque', label: L_DISQUE, formatsList: buildFormats('disque') },
    { id: 'calcul_cote', label: L_CALCUL, formatsList: buildFormats('calcul_cote') },
    { id: 'agrandissement', label: L_AGRANDISSEMENT, formatsList: buildFormats('agrandissement') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
